import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import InvoiceSheet, { InvoicePrintStyles, invoiceNumberFor } from '../components/InvoiceSheet';
import apiService from '../services/api';
import { getSchoolRevenue } from '../utils/transactionRevenue';
import { isReceivedTxn } from '../utils/funnel';
import { monthKeyFromTxn } from '../utils/schoolPay';
import '../styles/StaffPages.css';

/**
 * One school's payment statement for one month.
 *
 * Reuses InvoiceSheet so a statement and a merchant invoice are the same
 * document — the only difference is which side of the ledger it describes. The
 * "sent" banner is the one piece of state that is stored rather than computed.
 */

/**
 * The distinguishing part of a school's name, for the statement number.
 *
 * `invoiceNumberFor` keeps the first eight alphanumerics, which turns every
 * "University of ..." into the same UNIVERSI — the one part of the name that
 * identifies nothing. Dropping the leading form leaves the part that does.
 */
const distinctivePart = (name) =>
  String(name || '').replace(/^(the\s+)?(university|college|school|institute|academy)\s+of\s+/i, '').trim()
  || String(name || '');

const SuperuserSchoolPayStatement = () => {
  const { yearMonth, schoolId } = useParams();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [schools, setSchools] = useState([]);
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const currentUser = await apiService.fetchCurrentUser();
      if (!currentUser?.is_superuser) {
        navigate('/staff/dashboard/');
        return;
      }
      const [txnRes, schoolRes, overviewRes] = await Promise.all([
        apiService.getAllTransactionsSuperuser(),
        apiService.getSchools(),
        apiService.getSchoolPayOverview().catch(() => null),
      ]);
      setTransactions(txnRes?.transactions || []);
      setSchools(Array.isArray(schoolRes) ? schoolRes : schoolRes?.results || []);
      setStatement(
        (overviewRes?.statements || []).find(
          (s) => String(s.school) === String(schoolId) && s.period === yearMonth
        ) || null
      );
    } catch (err) {
      console.error('Failed to load statement:', err);
      setError('Failed to load transactions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, schoolId, yearMonth]);

  const school = useMemo(
    () => schools.find((s) => String(s.id) === String(schoolId)) || null,
    [schools, schoolId]
  );

  const lineItems = useMemo(() => {
    return transactions
      .filter((t) => {
        // Same gate as the list page: only commission the vendor has paid us.
        if (!isReceivedTxn(t)) return false;
        // And the same zero check. Without it the sheet lists rows the list page
        // never counted, so a statement could show more lines than the row it
        // was opened from claimed — and a school would read $0.00 entries it is
        // owed nothing for.
        if (!getSchoolRevenue(t)) return false;
        if (monthKeyFromTxn(t) !== yearMonth) return false;
        const s = t.school;
        const id = s && typeof s === 'object' ? s.id : s;
        return String(id) === String(schoolId);
      })
      .map((t) => {
        // The school is paid a share of the cashback the purchase earned, not
        // a share of the purchase. Showing the sale here would make the rate
        // read as sale x rate = share, which is not the arithmetic.
        const cashback = Number(t.commission_amount || 0);
        const share = getSchoolRevenue(t);
        return {
          id: t.id || t.external_txn_id,
          merchant:
            t.olive_merchant_name || t.wildfire_merchant_name || t.merchant_name || null,
          // When the money was collected, not when the purchase happened. The
          // statement is grouped by that month, so a purchase date here would
          // put dates on the sheet that fall outside the period it covers.
          date: t.received_at || t.captured_at || t.auth_at,
          status: t.status,
          amount: cashback,
          commission: share,
          // The school's cut of the cashback, not the merchant's rate.
          rate: t.school_commission_split ?? 0.7,
        };
      })
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [transactions, schoolId, yearMonth]);

  const totals = useMemo(() => {
    const totalSales = lineItems.reduce((s, l) => s + l.amount, 0);
    const totalCommission = lineItems.reduce((s, l) => s + l.commission, 0);
    return {
      totalSales,
      totalCommission,
      // A real blend rather than the first row's rate: splits are per
      // transaction, so a month can legitimately mix them.
      blendedRate: totalSales ? totalCommission / totalSales : (lineItems[0]?.rate ?? 0.7),
      count: lineItems.length,
    };
  }, [lineItems]);

  const markSent = async (resend = false) => {
    setSaving(true);
    setError(null);
    try {
      const saved = await apiService.markSchoolPaySent({
        school: schoolId,
        period: yearMonth,
        amount_cents: Math.round(totals.totalCommission * 100),
        transaction_count: totals.count,
        line_items: lineItems,
        resend,
      });
      setStatement(saved);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not mark this statement as sent.');
    } finally {
      setSaving(false);
    }
  };

  const clearSent = async () => {
    setSaving(true);
    setError(null);
    try {
      const saved = await apiService.unmarkSchoolPaySent({ school: schoolId, period: yearMonth });
      setStatement(saved);
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not clear the sent mark.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  const sent = Boolean(statement?.is_sent);
  const banner = (
    <div
      className="no-print"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap',
        padding: '16px 18px',
        borderRadius: '12px',
        border: `1px solid ${sent ? '#bbf7d0' : '#e5e7eb'}`,
        background: sent ? '#f0fdf4' : '#f9fafb',
        marginBottom: '20px',
        fontFamily: '"Baikal Book"',
        fontSize: '14px',
      }}
    >
      <div style={{ color: sent ? '#166534' : '#4b5563' }}>
        {sent ? (
          <>
            Marked sent on {new Date(statement.sent_at).toLocaleDateString()}.
            {' '}The figures below are recomputed live; the statement as sent is
            frozen on the record.
          </>
        ) : (
          'Not yet sent. Marking it freezes these figures against the month.'
        )}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        {sent ? (
          <button
            type="button"
            onClick={clearSent}
            disabled={saving}
            style={{
              padding: '10px 18px', borderRadius: '8px', border: '1px solid #e5e7eb',
              background: 'white', color: '#374151', cursor: 'pointer',
              fontFamily: '"Baikal Book"', fontSize: '14px',
            }}
          >
            {saving ? 'Working…' : 'Undo sent'}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => markSent(false)}
            disabled={saving || totals.count === 0}
            style={{
              padding: '10px 18px', borderRadius: '8px', border: 'none',
              background: '#00C8FF', color: '#000', cursor: 'pointer',
              fontFamily: '"Baikal Book"', fontSize: '14px', fontWeight: 500,
              opacity: saving || totals.count === 0 ? 0.6 : 1,
            }}
          >
            {saving ? 'Saving…' : 'Mark as sent'}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          {/* Money going out, not a bill going in: same document, opposite
              direction, so none of the invoice wording applies. A school is owed
              a share of what it earned — the cashback total and the split it was
              worked out from are our arithmetic, not theirs, so those columns
              are off. */}
          <InvoiceSheet
            backTo="/superuser/school-pay/"
            backText="Back to School Pay"
            banner={banner}
            merchantName={school ? school.name : 'Unknown school'}
            yearMonth={yearMonth}
            lineItems={lineItems}
            totals={totals}
            note="School share of commission earned this period."
            error={error || (!school ? 'That school no longer exists.' : null)}
            documentTitle="Payment Statement"
            documentNumber={invoiceNumberFor(yearMonth, distinctivePart(school?.name), 'HC-PS')}
            partyLabel="Pay To"
            periodLabel="Statement period"
            amountLabel="Amount Payable"
            commissionLabel="School Share"
            emptyText="No transactions for this school in this period."
            dateColumnLabel="Date collected"
            showMerchantColumn
            showBaseColumn={false}
            showRateColumn={false}
            evenColumns
          />
        </div>
      </div>
      <InvoicePrintStyles />
    </div>
  );
};

export default SuperuserSchoolPayStatement;
